<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { referralModule } from '@/store/referral/module'
import { stripeModule } from '@/store/stripe/module'
import { subscriptionModule } from '@/store/subscription/module'
import { userModule } from '@/store/user/module'
import { buildUTMParamString } from '@/store/utils'
import UIKit from '@pocketprep/ui-kit'
import ChoosePlan from '@/components/Register/ChoosePlan.vue'
import UpgradeModalHead from '@/components/UpgradeModal/UpgradeModalHead.vue'
import MountainSuccess from '@/components/MountainSuccess.vue'
import PaymentFields from '@/components/PaymentFields.vue'
import LicenseField from '@/components/Settings/ActivateLicense/LicenseField.vue'
import { upgradeModule } from '@/store/upgrade/module'
import { useRoute } from 'vue-router'
import * as Sentry from '@sentry/browser'
import { mobileModule } from '@/store/mobile/module'
import { licenseModule } from '@/store/license/module'

const route = useRoute()

const {
    Banner,
    Errors,
    Icon,
    Link: PocketLink,
    Input: PocketInput,
    Button: PocketButton,
    ModalContainer,
} = UIKit

const version = import.meta.env.VUE_APP_VERSION
const isLoading = ref(true)
const currentStep = ref(1)
const selectedPlanId = ref<string | null>(null)
const purchaseAttempted = ref(false)
const isShowingLicenseFields = ref(false)
const isLoadingLicenseActivation = ref(false)
const isShowingLicenseSuccess = ref(false)
const licenseKey = ref('')
const firstName = ref('')
const lastName = ref('')
const isLoadingPremiumActivation = ref(false)
const paymentFieldsRefreshTime = ref(Date.now())
const paymentError = ref('')
const premiumActivationError = ref('')
const isShowingPaymentSuccess = ref(false)
const impactClickId = ref('')

const upgradeContext = computed(() => {
    return upgradeModule.state.upgradeContext
})

const shouldReturnToMobileApp = computed(() => {
    return mobileModule.getters.isInArrivedFromMobileState()
})

const currentBundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const isFreeExam = computed(() => {
    return !!currentExamMetadata.value?.isFree
})

const mostRecentLicense = computed(() => {
    return licenseModule.getters.getMostRecentLicense()
})

const mostRecentLicenseOrgName = computed(() => {
    return mostRecentLicense.value?.organization && 'name' in mostRecentLicense.value.organization
        ? mostRecentLicense.value.organization.name
        : ''
})

const validReferral = computed(() => {
    return referralModule.getters.getValidReferral()
})

const referralError = computed(() => {
    return referralModule.getters.getReferralError()
})

const referralId = computed(() => {
    return !validReferral.value
        ? ''
        : 'objectId' in validReferral.value
            ? validReferral.value.objectId
            : validReferral.value.id
})

const stripePlansForBundle = computed(() => {
    return currentBundle.value
        ? stripeModule.getters.getStripePlansForBundle(currentBundle.value.objectId)
        : []
})

const sortedStripePlanOptions = computed(() => {
    return currentBundle.value && stripeModule.getters.getStripePlanOptions(currentBundle.value.objectId, true) || []
})

const selectedPlan = computed(() => {
    return stripePlansForBundle.value.find(plan => plan.id === selectedPlanId.value)
})

const planInfo = computed(() => {
    return {
        intervalCount: selectedPlan.value ? selectedPlan.value.interval_count : 1,
        interval: selectedPlan.value?.interval || 'month',
        amount: selectedPlan.value?.amount,
    }
})

const paymentPlatform = computed(() => {
    return upgradeModule.getters.getPaymentPlatform()
})

onMounted(async () => {
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
        stripeModule.actions.fetchStripePlans(),
        stripeModule.actions.fetchPaymentMethods(),
        userModule.actions.fetchUserData(),
    ])

    // If user already has a subscription, close the modal
    const hasSubscription = !!subscriptionModule.getters.getSubscriptionForExamId()
    if (hasSubscription) {
        await cancelUpgrade()
        return
    }
    
    // select a plan based on query param, if it exists and is valid
    const mobileProductIndex = mobileModule.getters.getProductIndex()
    const planIndex = mobileProductIndex === null ? 1 : mobileProductIndex
    const planOption = planIndex < sortedStripePlanOptions.value.length
        ? sortedStripePlanOptions.value[planIndex]
        : undefined
    if (!planOption) {
        Sentry.captureException(new Error('no planOptions bug with data'), {
            extra: {
                bundleId: currentBundle.value?.objectId,
                planOption: JSON.stringify(planOption),
                planOptions: JSON.stringify(sortedStripePlanOptions.value),
                currentBundle: JSON.stringify(currentBundle.value),
                stripePlans: JSON.stringify(stripeModule.state.plans.value),
                currentExamMetadata: JSON.stringify(examMetadataModule.getters.getCurrentExamMetadata()),
            },
        })
        throw new Error('UpgradeSidePanel - mounted: no planOptions')
    }
    if (typeof mobileProductIndex === 'number') {
        confirmPlan(planOption.value)
    } else {
        selectedPlanId.value = planOption.value
    }

    firstName.value = userModule.state.user?.firstName || ''
    lastName.value = userModule.state.user?.lastName || ''

    // check to see if there is a valid referral in the URL
    await referralModule.actions.validateReferral()

    if (import.meta.env.VUE_APP_IMPACT_UTT) {
        window.ire('generateClickId', (clickId: string) => {
            impactClickId.value = clickId
        })
    }

    isLoading.value = false
    focusTitle()
})

const confirmPlan = (planId: string) => {
    selectedPlanId.value = planId

    if (planId === 'free') {
        amplitudeUpgradeViewed()
        closeUpgradeModal()
    } else {
        currentStep.value = 2
        purchaseAttempted.value = true
        scrollTop()
    }
}

const showLicenseFields = () => {
    isShowingLicenseFields.value = true
    currentStep.value = 2
}

const activateLicense = async () => {
    if (firstName.value && lastName.value && licenseKey.value) {
        isLoadingLicenseActivation.value = true
        const license = await licenseModule.actions.validateLicense(licenseKey.value)
        await licenseModule.actions.activateLicense(license)
        // Updating the user also re-fetches user data and licenses
        await userModule.actions.updateUser({
            firstName: firstName.value,
            lastName: lastName.value,
        })
        await analyticsModule.actions.trackEvent('Upgrade_Viewed', {
            result: 'License Activated',
            ...(currentExamMetadata.value && { exam: currentExamMetadata.value.nativeAppName }),
        })

        isShowingLicenseSuccess.value = true
        isShowingLicenseFields.value = false
        isLoadingLicenseActivation.value = false

        // Scroll to top of page
        nextTick(() => {
            scrollTop()
        })
    }
}

const checkoutCompleted = async (checkoutSessionId: string) => {
    try {
        const checkoutSession = await stripeModule.actions.fetchCheckoutSession({
            checkoutSessionId: checkoutSessionId,
        })

        if (checkoutSession.status !== 'complete') {
            Sentry.captureException(new Error(`Incomplete checkout status for checkout session ${checkoutSessionId}`))
            paymentFieldsRefreshTime.value = Date.now()
            paymentError.value = 'Your payment failed or was canceled. Please try again.'
            nextTick(() => {
                scrollTop()
            })
            return
        }

        isLoadingPremiumActivation.value = true
        isShowingPaymentSuccess.value = true

        const subscriptionId = checkoutSession.subscription as string | null

        if (!subscriptionId) {
            throw new Error(`Missing subscription from checkout session ${checkoutSessionId}: `)
        }

        await subscriptionModule.actions.activateSubscription({
            subscriptionId: subscriptionId,
            paymentPlatform: paymentPlatform.value,
        })

        await userModule.actions.updateWebConfig({
            hasSeenWelcomeModal: true,
        })

        await analyticsModule.actions.trackEvent('Upgrade_Viewed', {
            result: 'Subscribed',
            ...(currentExamMetadata.value && { exam: currentExamMetadata.value.nativeAppName }),
        })
    } catch (err) {
        Sentry.captureException(err)
        premiumActivationError.value =
            'There was an error updating your account. Try refreshing your page or contacting support.'
        await userModule.actions.fetchUserData({ stripeSubs: true })
    }
    isLoadingPremiumActivation.value = false
}

const handlePaymentFieldsError = async () => {
    paymentError.value = 'Error loading payment fields. Try refreshing your page or contacting support.'
    await userModule.actions.fetchUserData({ stripeSubs: true })
}

const cancelUpgrade = async () => {
    if (isLoadingPremiumActivation.value || isLoadingLicenseActivation.value) {
        return
    }

    await analyticsModule.actions.trackEvent('Upgrade_Viewed', {
        result: 'Free',
        ...(currentExamMetadata.value && { exam: currentExamMetadata.value?.nativeAppName }),
    })

    if (validReferral.value || referralError.value) {
        referralModule.actions.trackHasSeenReferralUpgradePanel()
    }

    closeUpgradeModal({ cancel: true })
}

const closeUpgradeModal = (options?: { cancel?: boolean }) => {
    amplitudeUpgradeViewed()

    // IF we arrive from a mobile app, head back there
    if (shouldReturnToMobileApp.value) {
        // we can only send back to mobile app within 1 second of user clicking the action
        // so we can send back instantly on cancel, but if user upgrades we need to prompt the action in modal
        if (options?.cancel) {
            mobileModule.actions.returnToMobileApp()
        } else {
            mobileModule.actions.triggerReturnToMobileModal()
        }
    }
    upgradeModule.actions.closeUpgradeModal()
}

const goBack = () => {
    const newStep = currentStep.value - 1
    currentStep.value = Math.max(newStep, 1)
    isShowingLicenseFields.value = false
    focusTitle()
}

const focusTitle = () => {
    setTimeout(() => {
        const titleEl = document.querySelector('.upgrade-modal__title') as HTMLElement
        titleEl?.focus()
    }, 1)
}

const scrollTop = () => {
    const registerEl = document.querySelector('.upgrade-modal')
    registerEl?.scrollTo(0, 0)
}

const getNumOfSubscriptionDays = () => {
    if (planInfo.value?.interval === 'month') {
        return planInfo.value.intervalCount * 30
    }

    if (planInfo.value?.interval === 'year') {
        return 365
    }

    return 0
}

const getQuizModeScreenName = () => {
    const quizMode = upgradeContext.value?.quizMode

    if (quizMode === 'missed') {
        return 'Missed Quiz'
    } else if (quizMode === 'weakest') {
        return 'Weakest Quiz'
    }

    return null
}

const amplitudeUpgradeViewed = () => {
    // build out properties for Upgrade Viewed event
    const platform = mobileModule.getters.getPlatform() || 'Web Study'
    const upgradePurchasePressed = upgradeContext.value?.upgradePurchasePressed ? true : purchaseAttempted.value
    const upgradePriorScreen = mobileModule.getters.getPriorScreenName()
        || getQuizModeScreenName()
        || analyticsModule.getters.getScreenName(route)
    const didSubscribe = !!subscriptionModule.getters.getSubscriptionForExamId()
    const numOfSubscriptionDays = didSubscribe && !licenseKey.value ? getNumOfSubscriptionDays() : 0

    analyticsModule.actions.amplitudeTrack('Upgrade Viewed', {
        bundleId: currentBundle.value?.objectId,
        client: platform,
        clientAppId: 'study.pocketprep.com',
        clientVersion: version,
        examGuid: currentExamMetadata.value?.examGuid,
        organizationId: userModule.getters.getCurrentOrganizationId() || undefined,
        userDomain: userModule.getters.getUserDomain() || undefined,
        upgradeDidSubscribe: licenseKey.value ? false : didSubscribe,
        upgradePurchasePressed,
        upgradePriorScreen,
        upgradeSubscriptionDays: numOfSubscriptionDays,
        upgradeLicenseCodeUsed: licenseKey.value || undefined,
    })
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            class="upgrade-modal__container"
            key="upgradeModal"
            :show-close-button="!isShowingPaymentSuccess && !isShowingLicenseSuccess"
            @close="cancelUpgrade"
        >
            <template #head>
                <UpgradeModalHead
                    v-if="!isShowingPaymentSuccess && !isShowingLicenseSuccess"
                    :is-free-exam="isFreeExam"
                    :is-showing-license-fields="isShowingLicenseFields"
                    :current-step="currentStep"
                    @back="goBack"
                />
            </template>
            <div 
                class="upgrade-modal"
                :class="{
                    'upgrade-modal--success': isShowingPaymentSuccess || isShowingLicenseSuccess,
                }"
            >
                <template v-if="!isLoading">
                    <Banner
                        v-if="validReferral && currentStep === 1"
                        class="upgrade-modal__banner"
                    >
                        <template #bannerText>
                            <template v-if="'name' in validReferral && validReferral.name">
                                {{ validReferral.name }}
                            </template>
                            <template v-else-if="'objectId' in validReferral">
                                Someone sent you 20% off your first subscription with Pocket Prep. Sweet!
                            </template>
                        </template>
                    </Banner>
                    <Errors
                        v-else-if="!validReferral && referralError && currentStep === 1"
                        :errors="[ referralError ]"
                        class="upgrade-modal__error-banner"
                    />
                    <h1
                        v-if="!isShowingPaymentSuccess && !isShowingLicenseSuccess"
                        class="upgrade-modal__title"
                        tabindex="-1"
                        role="heading"
                    >
                        <template v-if="currentStep === 1">
                            Choose Your Plan
                        </template>
                        <template v-else-if="currentStep === 2 && !isShowingLicenseFields">
                            Add Payment & Review
                        </template>
                        <template v-else-if="currentStep === 2 && isShowingLicenseFields">
                            Add License Code
                        </template>
                    </h1>
                    <div
                        v-if="!isShowingPaymentSuccess && !isShowingLicenseSuccess && currentStep === 2"
                        class="upgrade-modal__subtitle"
                        :class="{
                            'upgrade-modal__subtitle--error': paymentError,
                        }"
                    >
                        <template v-if="paymentError">
                            {{ paymentError }}
                        </template>
                        <template v-else-if="isShowingLicenseFields">
                            License codes are purchased by your school or organization.
                            If you haven't received a license code, contact your organization.
                        </template>
                        <template v-else>
                            Last step! You can cancel anytime.
                        </template>
                    </div>
                    <ChoosePlan
                        v-if="currentStep === 1 && currentExamMetadata"
                        :exam="currentExamMetadata"
                        :bundle="currentBundle"
                        :saved-selected-plan-id="selectedPlanId"
                        :valid-referral="validReferral"
                        :referral-error="referralError"
                        @confirmPlan="confirmPlan"
                    >
                        <template #secondaryAction>
                            <PocketButton
                                class="upgrade-modal__add-license-code-btn"
                                type="secondary"
                                @click="showLicenseFields"
                            >
                                Add License Code
                            </PocketButton>
                        </template>
                    </ChoosePlan>
                    <div v-if="currentStep === 2 && isShowingLicenseFields">
                        <div class="upgrade-modal__license-fields">
                            <PocketInput
                                v-model="firstName"
                                label="First Name"
                                class="upgrade-modal__input"
                            />
                            <PocketInput
                                v-model="lastName"
                                label="Last Name"
                                class="upgrade-modal__input"
                            />
                            <LicenseField
                                class="upgrade-modal__license-field"
                                v-model="licenseKey"
                            />
                            <PocketButton
                                class="upgrade-modal__activate-license-btn"
                                :disabled="!firstName || !lastName || !licenseKey"
                                :is-loading="isLoadingLicenseActivation"
                                @click="activateLicense"
                            >
                                Activate License
                            </PocketButton>
                            <div
                                class="upgrade-modal__license-footer"
                                :class="{
                                    'upgrade-modal__license-footer--static': !!licenseKey,
                                }"
                            >
                                Learn more about
                                <PocketLink
                                    :href="`https://www.pocketprep.com/for-educators?${
                                        buildUTMParamString({
                                            utm_campaign: 'app_settings',
                                            utm_content: 'for_educators',
                                        })
                                    }`"
                                    target="_blank"
                                    class="upgrade-modal__for-educators-link"
                                    aria-label="Pocket Prep for Educators. Click to open page in new tab."
                                    @mousedown.prevent
                                >
                                    Pocket Prep for Educators
                                </PocketLink>
                            </div>
                        </div>
                    </div>
                    <PaymentFields
                        v-else-if="currentStep === 2
                            && selectedPlanId
                            && selectedPlanId !== 'free'
                            && currentExamMetadata
                            && !isShowingLicenseSuccess
                            && !isShowingPaymentSuccess"
                        mode="subscription"
                        :key="paymentFieldsRefreshTime"
                        :price-id="selectedPlanId"
                        :referral-id="referralId"
                        :impact-click-id="impactClickId"
                        @checkoutCompleted="checkoutCompleted"
                        @error="handlePaymentFieldsError"
                    />
                    <MountainSuccess
                        v-else-if="isShowingPaymentSuccess || isShowingLicenseSuccess"
                        class="upgrade-modal__mountain-success"
                        :is-loading="isLoadingPremiumActivation || isLoadingLicenseActivation"
                        :errorMessage="premiumActivationError"
                        @close="closeUpgradeModal"
                    >
                        <template #successTitle>
                            <div>You're Premium Now</div>
                        </template>
                        <template #successMessage>
                            <template v-if="isShowingPaymentSuccess">
                                <div class="upgrade-modal__success-message-paragraph">
                                    You're in good company.
                                    91,695 learners are using Pocket Prep Premium to pass their exams—just like you.
                                </div>
                            </template>
                            <template v-else-if="isShowingLicenseSuccess">
                                <div class="upgrade-modal__success-message-paragraph">
                                    Woo! Thanks to your organization, {{
                                        mostRecentLicenseOrgName
                                            ? `${mostRecentLicenseOrgName}, `
                                            : ''
                                    }}you have access to the full question bank and all the latest features!
                                </div>
                                <div class="upgrade-modal__success-message-paragraph">
                                    Your study activity is visible to your organization.
                                </div>
                            </template>
                        </template>
                    </MountainSuccess>
                </template>
                <div 
                    class="upgrade-modal__financial-support" 
                    v-if="!isLoading && currentStep === 1"
                >
                    <div class="upgrade-modal__financial-support-message">
                        Need help paying for Premium? Learn about our
                        <PocketLink
                            :href="`https://www.pocketprep.com/pocket-prep-financial-support-program?${
                                buildUTMParamString({
                                    utm_campaign: 'app_settings',
                                    utm_content: 'financial_support_program',
                                })
                            }`"
                            target="_blank"
                            class="upgrade-modal__financial-support-link"
                            aria-label="Financial Support Program. Open help center article in browser view."
                            @mousedown.prevent
                        >
                            Financial Support Program
                            <Icon type="externalLink" />
                        </PocketLink>
                    </div>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.upgrade-modal {
    width: 1010px;
    background: $gray-background;
    height: calc(80vh - 56px);
    min-height: 640px;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 0 0 12px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;

    @include breakpoint(brown-bear) {
        width: 740px;
        height: calc(100vh - 140px) !important;
    }

    @include breakpoint(black-bear) {
        max-height: none;
        height: calc(100vh - 56px) !important;
        width: 100%;
        min-height: 540px;
    }

    &--success {
        overflow: hidden;

        @include breakpoint(black-bear) {
            height: 100% !important;
        }
    }

    &__container {
        :deep(.uikit-modal__close) {
            top: 13px;
        }
    }

    &__banner {
        max-width: 600px;
        height: auto;
        margin-top: 24px;

        @include breakpoint(black-bear) {
            max-width: 350px;
        }
    }

    &__error-banner {
        margin-top: 24px;
        max-width: 550px;

        @include breakpoint(black-bear) {
            margin-top: 16px;
            max-width: 300px;
        }
    }

    &__step {
        font-size: 13px;
        line-height: 18px;
        text-align: center;
        color: $slate-03;
        padding-top: 46px;
        margin-bottom: 10px;

        &--referral {
            padding-top: 40px;
        }

        @include breakpoint (black-bear) {
            padding-top: 21px;
        }
    }

    &__title {
        font-size: 26px;
        outline: none;
        line-height: 36px;
        font-weight: 700;
        text-align: center;
        margin: 0;
        margin-top: 36px;
        padding: 0 25px;

        @include breakpoint(black-bear) {
            max-width: 323px;
            margin-top: 24px;
        }
    }

    &__subtitle {
        color: #475967;
        font-size: 14px;
        line-height: 19px;
        text-align: center;
        margin-bottom: 36px;
        margin-top: 4px;
        width: 350px;

        @include breakpoint(brown-bear) {
            margin-bottom: 4px;
        }

        &--error {
            color: $pepper;
        }
    }

    &__add-license-code-btn {
        @include breakpoint(black-bear) {
            margin-top: 16px
        }
    }

    &__license-fields {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 320px;
        margin-top: 14px;
    }

    &__input {
        width: 100%;
        margin-bottom: 18px;
    }

    &__license-field {
        width: 296px;
    }

    &__activate-license-btn {
        margin-top: 13px;
    }

    &__license-footer {
        position: absolute;
        bottom: 40px;
        font-size: 13px;
        line-height: 18px;

        &--static {
            position: static;
            bottom: initial;
            margin-top: 32px;
            margin-bottom: 40px;
        }
    }

    &__for-educators-link {
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
    }

    &__mountain-success {
        position: relative;
        width: 100%;
        height: 100%;
        background-color: $floral-white;
        padding-top: 78px;
        padding-bottom: 32px;

        @include breakpoint(black-bear) {
            padding-top: 42px;
        }
    }

    &__success-message-paragraph {
        margin-top: 12px;
    }

    &__financial-support {
        display: flex;
        width: 100%;
        align-items: center;
        align-content: center;
        padding-top: 14px;
        padding-bottom: 14px;
        color: $slate;
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        border-radius: 0px 0px 12px 12px;

        @include breakpoint(black-bear) {
            height: 66px;
        }
    }

    &__financial-support-message {
        margin-left: 80px;

        @include breakpoint(black-bear) {
            margin-left: 27px;
            margin-right: 28px;
        }
    }

    &__financial-support-link {
        font-size: 13px;
        font-style: normal;
        font-weight: 500;
        line-height: 18px;

        svg {
            margin-left: 3px;
            width: 9px;
            height: 10px;
        }
    }
}
</style>
